// ReviewItemPanel — the focused side drawer that hosts the rich review UI for
// a single "Needs Review" item.
//
// Opened from two surfaces — the Agents Kanban's "Needs Review" column and the
// global "Agent activity" drawer — so the open/close state lives in a context
// (<ReviewPanelProvider>, mounted once at the root) and the panel itself is
// mounted once next to <RunDetailPanel>. Cards anywhere call `openReview(item)`.
// Mirrors <RunDetailPanel>'s shell (overlay, focus trap, Escape-to-close,
// close-on-route-change) and sits at z-[60] so it stacks above the z-50 agent
// activity drawer when opened from inside it.

import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
    type ReactNode,
} from 'react';
import { Link, useLocation } from 'react-router';
import { ExternalLink, Bot, X } from 'lucide-react';
import { useFocusTrap } from '@/lib/hooks';
import { useSession } from '@/contexts/SessionContext';
import { canOpenRoute } from '@/lib/frontends';
import { cn } from '@/lib/utils';
import type { ReviewQueueItem } from '@/lib/use-review-queue';
import {
    CallDraftsBody,
    CoacActionDraftBody,
    ComposerDraftBody,
    MonthlyNarrativeBody,
    TaskBody,
} from './review-item-bodies';

// Body kinds that render their own bottom-pinned sticky footer (e.g. the
// call-drafts batch Confirm bar). The scroll container drops its bottom padding
// for these so the footer sits flush to the scrollport with no gap or wobble.
const BODIES_WITH_STICKY_FOOTER = new Set<ReviewQueueItem['kind']>(['call_drafts']);

// -------- open/close context --------

interface ReviewPanelContextValue {
    openReview: (item: ReviewQueueItem) => void;
    closeReview: () => void;
}

const ReviewPanelContext = createContext<ReviewPanelContextValue | null>(null);
// Selected item lives in its own context so cards that only *open* the panel do
// not re-render when the selection changes.
const ReviewPanelItemContext = createContext<ReviewQueueItem | null>(null);

export function ReviewPanelProvider({ children }: { children: ReactNode }) {
    const [item, setItem] = useState<ReviewQueueItem | null>(null);
    const openReview = useCallback((next: ReviewQueueItem) => setItem(next), []);
    const closeReview = useCallback(() => setItem(null), []);
    const value = useMemo<ReviewPanelContextValue>(
        () => ({ openReview, closeReview }),
        [openReview, closeReview],
    );
    return (
        <ReviewPanelContext.Provider value={value}>
            <ReviewPanelItemContext.Provider value={item}>
                {children}
            </ReviewPanelItemContext.Provider>
        </ReviewPanelContext.Provider>
    );
}

export function useReviewPanel(): ReviewPanelContextValue {
    const value = useContext(ReviewPanelContext);
    if (!value) throw new Error('useReviewPanel must be used within a ReviewPanelProvider');
    return value;
}

// -------- panel shell --------

export default function ReviewItemPanel() {
    const item = useContext(ReviewPanelItemContext);
    const { closeReview } = useReviewPanel();
    const location = useLocation();
    const open = item != null;
    const panelRef = useRef<HTMLDivElement>(null);

    // Retain the last item while the drawer slides shut so the content does not
    // blank out mid-transition.
    const [displayItem, setDisplayItem] = useState<ReviewQueueItem | null>(item);
    useEffect(() => {
        if (item) setDisplayItem(item);
    }, [item]);

    useFocusTrap(panelRef, open);

    useEffect(() => {
        if (!open) return;
        const handler = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                event.stopPropagation();
                closeReview();
            }
        };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, [open, closeReview]);

    // Close when the route changes underneath the panel (e.g. an "Open in
    // context" link), matching <RunDetailPanel>'s behaviour.
    const pathRef = useRef(location.pathname);
    useEffect(() => {
        if (pathRef.current === location.pathname) return;
        pathRef.current = location.pathname;
        closeReview();
    }, [location.pathname, closeReview]);

    return (
        <div
            data-testid="review-item-panel"
            data-state={open ? 'open' : 'closed'}
            className={cn('fixed inset-0 z-[60]', !open && 'pointer-events-none')}
            aria-hidden={!open}
        >
            <div
                className={cn(
                    'absolute inset-0 bg-[var(--septima-scrim)]',
                    'transition-opacity duration-popover ease-swiss',
                    open ? 'opacity-100' : 'opacity-0',
                )}
                onClick={closeReview}
            />

            <div
                ref={panelRef}
                role="dialog"
                aria-modal="true"
                aria-labelledby="review-item-panel-title"
                className={cn(
                    'absolute right-0 top-0 h-full w-full lg:w-[680px] xl:w-[760px]',
                    'bg-[rgb(var(--color-page))] flex flex-col',
                    'border-l border-[rgb(var(--color-divider))]',
                    'transform transition-transform duration-popover ease-swiss',
                    open ? 'translate-x-0' : 'translate-x-full',
                )}
            >
                {displayItem && (
                    <>
                        <PanelHeader item={displayItem} onClose={closeReview} />
                        <div
                            className={cn(
                                'flex-1 overflow-y-auto px-card-padding pt-md',
                                // Bodies with a sticky Confirm footer need it
                                // pinned flush to the scrollport bottom; a bottom
                                // padding here would leave a gap the list shows
                                // through. Drop it for those; keep symmetric
                                // breathing room for the rest.
                                BODIES_WITH_STICKY_FOOTER.has(displayItem.kind) ? 'pb-0' : 'pb-md',
                            )}
                        >
                            {/* The key is load-bearing (SEP-230). Each body
                                seeds its edit state from its item one time
                                only, and the panel stays mounted between
                                items. Without the key, the next item reuses
                                the body instance, and an approval writes the
                                previous card's edits onto the new card. */}
                            <PanelBody item={displayItem} key={displayItem.id} />
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

function PanelHeader({ item, onClose }: { item: ReviewQueueItem; onClose: () => void }) {
    const { user } = useSession();
    const canOpen = canOpenRoute(item.contextHref, user?.personas ?? []);
    return (
        <div className="sticky top-0 z-10 bg-[rgb(var(--color-page))] border-b border-[rgb(var(--color-divider))]">
            <div className="flex items-start justify-between gap-3 px-card-padding pt-md pb-sm">
                <div className="flex items-start gap-2 min-w-0">
                    <Bot className="w-4 h-4 mt-0.5 text-[rgb(var(--color-accent))] flex-shrink-0" />
                    <div className="flex flex-col gap-0.5 min-w-0">
                        <span className="text-annotation font-mono tabular-nums text-[rgb(var(--color-text-tertiary))]">
                            {item.agent}
                        </span>
                        <h2
                            id="review-item-panel-title"
                            className="text-section-title text-[rgb(var(--color-text-primary))] truncate"
                        >
                            {item.primaryLabel}
                        </h2>
                        <span className="text-annotation text-[rgb(var(--color-text-secondary))] truncate">
                            {item.secondaryLabel}
                        </span>
                    </div>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                    {canOpen && (
                        <Link
                            to={item.contextHref}
                            onClick={onClose}
                            title="Open in context"
                            className={cn(
                                'inline-flex items-center gap-1 px-2 py-1 rounded-button',
                                'text-annotation text-[rgb(var(--color-text-secondary))]',
                                'hover:bg-[rgb(var(--color-surface-hover)/0.05)]',
                                'hover:text-[rgb(var(--color-text-primary))]',
                                'transition-colors duration-card-hover ease-swiss',
                            )}
                        >
                            <ExternalLink className="w-3.5 h-3.5" />
                            <span className="hidden sm:inline">Open</span>
                        </Link>
                    )}
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label="Close review panel"
                        className={cn(
                            'w-7 h-7 flex items-center justify-center rounded-button',
                            'text-[rgb(var(--color-text-secondary))]',
                            'transition-colors duration-card-hover ease-swiss',
                            'hover:bg-[rgb(var(--color-surface-hover)/0.05)]',
                            'hover:text-[rgb(var(--color-text-primary))]',
                        )}
                    >
                        <X className="w-4 h-4" strokeWidth={1.5} />
                    </button>
                </div>
            </div>
        </div>
    );
}

function PanelBody({ item }: { item: ReviewQueueItem }) {
    // Close the drawer once a decision commits (single-decision bodies fire on
    // success; the call-drafts body fires after its batch Confirm).
    const { closeReview } = useReviewPanel();
    switch (item.kind) {
        case 'call_drafts':
            return <CallDraftsBody item={item} showReviewed={false} onCommitted={closeReview} />;
        case 'monthly_narrative':
            return (
                <MonthlyNarrativeBody item={item} showReviewed={false} onCommitted={closeReview} />
            );
        case 'coac_action_draft':
            return (
                <CoacActionDraftBody item={item} showReviewed={false} onCommitted={closeReview} />
            );
        case 'composer_draft':
            return <ComposerDraftBody item={item} showReviewed={false} onCommitted={closeReview} />;
        case 'task':
            return <TaskBody item={item} onCommitted={closeReview} />;
    }
}
